import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  ChartPieIcon,
  TableCellsIcon,
} from '@heroicons/react/16/solid';
import { userAppContext, searchContext } from '../../../../../../context/Context';
import { useRightSidebarLayout } from '../../../context/RightSidebarLayoutContext';
import { SearchContext } from '../../../../../../types';
import { SidebarSearchDropdown } from './SidebarSearchDropdown';
import { getFilters } from '../../../../../../services/savedFiltersService';
import { encodeData } from '../../../../../../utils/encodeUtils';
import { UserContext } from '../../../../../../types';
import { Filter, SavedSearch } from '../../../../../../components/ProfileArea/types';
import {
  filterColumnFilters,
  determineSearchType,
} from '../../../../../../components/ProfileArea/favoritesUtils';
import { getTitle, getName, DATASETS } from '../../../../../../components/Constants';
import { SidebarSources } from './SidebarSources';
import { SidebarColumns } from './SidebarColumns';
import SavedScoresDropdown from '../../dropdown/SavedScoresDropdown';
import { NAVBAR_HEIGHT_PX } from '@/router/AppRouter';
import SharedButton from '@/global/Button/Button';
import useScrollWindow from '@/hooks/useScrollWindow';
import SavedColumnsBar from '@/components/UI/SavedColumnsBar';
import { chemicalRestrictedColumns, defaultModel } from '../../../constants';
import { MappingContent } from '../types';
import { ColumnVisibilityModels } from '@/components/Table/ColumnVisibilityModels';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { GoInfo } from 'react-icons/go';
import { useMappingStackStore } from '@/store';
import { getViewToggleText } from '../../../helpers/graphHelpers';

function loadSavedSearches(): Promise<SavedSearch[]> {
  const cloudPromise = getFilters()
    .then(response => {
      const cloudFilters = response.filters || [];
      return cloudFilters.map((filter: any) => ({
        id: filter.filterId,
        table: filter.type === 'score' ? 'Score' : filter.isEvidence ? 'Evidence' : 'Summary',
        filters: filter.filters,
        searchQuery: filter.searchQuery,
        savedAt: filter.createdAt,
        name: filter.name,
        type: determineSearchType(filter),
        columnOrder: filter.columnOrder,
        columnVisibilityModel: filter.columnVisibilityModel,
        searchContext: filter.searchContext,
        sourceWeights: filter.sourceWeights,
        isLocal: false,
      }));
    })
    .catch(() => []);

  const localColumnSelections: SavedSearch[] = [];
  const localScores: SavedSearch[] = [];
  Object.keys(localStorage).forEach((key: string) => {
    if (key.startsWith('columnSelection_') || key.startsWith('completeSearch_')) {
      try {
        const selection = JSON.parse(localStorage.getItem(key) || '');
        localColumnSelections.push({
          id: key,
          table: selection.searchContext?.isEvidence ? 'Evidence' : 'Summary',
          filters: selection.columnFilters || selection.columnVisibilityModel || [],
          searchQuery: selection.searchQuery || null,
          savedAt: selection.savedAt,
          name: selection.name,
          type: key.startsWith('completeSearch_') ? 'complete' : 'columns',
          columnOrder: selection.columnOrder,
          columnVisibilityModel: selection.columnVisibilityModel,
          searchContext: selection.searchContext,
          isLocal: true,
        });
      } catch {
        // ignore
      }
    }
    if (key.startsWith('savedScore_')) {
      try {
        const scoreData = JSON.parse(localStorage.getItem(key) || '');
        localScores.push({
          id: key,
          table: 'Score',
          filters: [],
          searchQuery: null,
          savedAt: scoreData.savedAt,
          name: scoreData.name,
          type: 'score',
          sourceWeights: scoreData.sourceWeights,
          searchContext: scoreData.searchContext,
          isLocal: true,
        });
      } catch {
        // ignore
      }
    }
  });

  return cloudPromise.then(searches => {
    const all = [...searches, ...localColumnSelections, ...localScores];
    return all.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  });
}

function buildSearchUrl(search: SavedSearch): string | null {
  try {
    const url = new URL(`${window.location.origin}/search`);
    const filters = search.filters || [];
    const isEvidence = search.table === 'Evidence';

    const getColumnFiltersForUrl = (): Filter[] => {
      const column = filters.filter(
        (f: Filter) =>
          !['view', 'tab', 'source', 'idents'].includes(f.name) &&
          f.value !== undefined &&
          f.value !== null &&
          f.value !== '',
      );
      return column.length > 0 ? column : filterColumnFilters(filters);
    };

    const setEncodedFiltersOnUrl = (columnFilters: Filter[]) => {
      if (columnFilters.length === 0) return;
      const filtersWithEvidence = columnFilters.map((f: Filter) => ({
        ...f,
        isEvidence,
        operator: f.operator ?? 'eq',
        type: f.type ?? 'string',
      }));
      url.searchParams.set('f', encodeData(filtersWithEvidence));
    };

    if (search.searchQuery) {
      const searchParams = new URLSearchParams(search.searchQuery);
      searchParams.forEach((value, key) => url.searchParams.set(key, value));
      const columnFilters = getColumnFiltersForUrl();
      setEncodedFiltersOnUrl(columnFilters);
      return url.href;
    }

    if (filters.length === 0) return null;

    const getFilterValue = (name: string, defaultValue: string) =>
      filters.find((f: Filter) => f.name === name)?.value || defaultValue;

    url.searchParams.set('view', getFilterValue('view', 'ALL'));
    url.searchParams.set('tab', getFilterValue('tab', 'GDA'));
    url.searchParams.set('source', getFilterValue('source', 'ALL'));
    url.searchParams.set('idents', getFilterValue('idents', 'ALL'));

    const columnFilters = getColumnFiltersForUrl();
    setEncodedFiltersOnUrl(columnFilters);

    return url.href;
  } catch {
    return null;
  }
}

export interface RightAsideSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  source: string;
  sourceWeights: Array<{ name: string; score: number }>;
  view: string;
  tab: string;
  isEvidence?: boolean;
  columnVisibilityModel: Record<string, boolean>;
  setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  searchItems?: any[];
  onOpenSearchItemInfo?: (item: any) => void;
  onRemoveSearchItem?: (index: number) => void;
  onClearAllSearchItems?: () => void;
  isMapping?: boolean;
  columnPanelToggle?: { active: 'summary' | 'evidence'; onChange: (active: 'summary' | 'evidence') => void };
  /** My Score section (GDA/VDA, authenticated, non-academic) */
  showMyScoreSection?: boolean;
  showSavedColumnsSection?: boolean;
  onApplySavedColumns?: (columns: any) => void;
  onDeleteSavedColumns?: (id: string) => void;
  onOpenScoreModal?: () => void;
  onApplySavedScore?: (score: any) => void;
  onDeleteSavedScore?: (id: string) => void;
  onClearWeights?: () => void;
  hasActiveWeights?: boolean;
  refreshScoresDropdown?: number;
  mappingContent?: MappingContent;
  isAcademic?: boolean;
  columnOrder?: string[];
  onResetToDefault?: () => void;
}

function SidebarSection({
  title,
  children,
  className,
  defaultOpen = false,
  growWithContent = false,
  lockedOverlay,
  expandSignal,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
  growWithContent?: boolean;
  lockedOverlay?: React.ReactNode;
  expandSignal?: number;
}) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (expandSignal !== undefined && expandSignal > 0) {
      setOpen(true);
    }
  }, [expandSignal]);
  return (
    <section className={clsx('flex flex-col flex-shrink-0 border-b border-gray-100 min-w-0', className)}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-2 w-full min-w-0 px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/80 hover:bg-gray-100 flex-shrink-0 text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
        )}
        <span className="truncate">{title}</span>
      </button>
      {open && (
        <div
          className={clsx(
            'relative min-w-0 px-1 flex flex-col',
            growWithContent ? 'flex-none overflow-visible' : 'flex-1 min-h-0 overflow-hidden',
          )}
        >
          {children}
          {lockedOverlay && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 locked-overlay">
              {lockedOverlay}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function RightAsideSidebar({
  isOpen,
  onClose,
  source,
  sourceWeights,
  view,
  tab,
  isEvidence = false,
  columnVisibilityModel,
  setColumnVisibilityModel,
  searchItems = [],
  onOpenSearchItemInfo,
  onRemoveSearchItem,
  onClearAllSearchItems,
  isMapping = false,
  columnPanelToggle,
  showMyScoreSection = false,
  onApplySavedColumns,
  onDeleteSavedColumns,
  onOpenScoreModal,
  onApplySavedScore,
  onDeleteSavedScore,
  onClearWeights,
  hasActiveWeights = false,
  refreshScoresDropdown = 0,
  mappingContent,
  isAcademic,
  columnOrder,
  onResetToDefault,
}: RightAsideSidebarProps) {
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { isAuthenticated, restrictGraphDisplay } = userAppContext() as unknown as UserContext;
  const searchContextValue = searchContext() as unknown as SearchContext;
  const { setSearchItems: contextSetSearchItems, searchItems: contextSearchItems } = searchContextValue ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSidebarOpen, searchItemsSectionExpandSignal } = useRightSidebarLayout();
  const { isMobile, isTablet } = useScrollWindow();
  const entityListTabs = ['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'];
  const showManageGraph = view !== 'ALL' && view !== 'CHEMICALS' && !entityListTabs.includes(tab as string);
  const [showCurrentItems, setShowCurrentItems] = useState(true);

  const isDesktop = !isMobile && !isTablet;

  const [favorites, setFavorites] = useState<SavedSearch[]>([]);
  const savedColumns = favorites.filter(f => f.type === 'columns');
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [, setSidebarDropdownOpen] = useState(false);
  const mappingTabSegment = (mappingContent as any)?.tabSegment;
  const collapseMappingToRoot = useMappingStackStore(s => s.collapseToRootLevel);
  const effectiveSidebarTab =
    isMapping && typeof mappingTabSegment === 'string' && mappingTabSegment.length > 0
      ? mappingTabSegment.toUpperCase()
      : tab;

  const selectedView: 'tables' | 'graphs' = searchParams.get('display') === 'graphs' ? 'graphs' : 'tables';

  const [pendingItems, setPendingItems] = useState<any[]>(contextSearchItems ?? []);
  useEffect(() => {
    setPendingItems(contextSearchItems ?? []);
  }, [contextSearchItems]);

  useEffect(() => {
    if (!isAuthenticated) {
      setFavorites([]);
      return;
    }
    setFavoritesLoading(true);
    loadSavedSearches()
      .then(setFavorites)
      .finally(() => setFavoritesLoading(false));
  }, [isAuthenticated, isOpen]);

  const handleViewChange = () => {
    const prevView = selectedView;
    const nextView = prevView === 'tables' ? 'graphs' : 'tables';
    if (prevView === 'tables' && nextView === 'graphs') {
      collapseMappingToRoot();
    }
    setSearchParams(
      prev => {
        const updated = new URLSearchParams(prev);
        if (nextView === 'tables') {
          updated.delete('display');
        } else {
          updated.set('display', nextView);
        }
        return updated;
      },
      { replace: true },
    );
  };

  const handleFavoriteClick = (search: SavedSearch) => {
    if (search.type === 'score' && search.searchContext && search.sourceWeights) {
      const { view: v, tab: t, source: s, searchItems } = search.searchContext;
      const url = new URL(`${window.location.origin}/search`);
      url.searchParams.set('view', v || 'ALL');
      url.searchParams.set('tab', t || 'GDA');
      url.searchParams.set('source', search.sourceWeights.length > 1 ? 'ALL' : s || 'ALL');
      if (searchItems?.length) {
        url.searchParams.set('idents', searchItems.map(item => item.id).join(','));
      } else {
        url.searchParams.set('idents', 'ALL');
      }
      sessionStorage.setItem(`sourceWeights_${t}`, JSON.stringify(search.sourceWeights));
      navigate(url.href.replace(window.location.origin, ''));
      onClose();
      return;
    }

    if ((search.type === 'columns' || search.type === 'complete') && search.searchContext) {
      const { view: v, tab: t, source: s, searchItems: items } = search.searchContext;
      const url = new URL(`${window.location.origin}/search`);
      const isComplete = search.type === 'complete';
      if (isComplete && search.searchQuery) {
        const searchParams = new URLSearchParams(search.searchQuery);
        searchParams.forEach((value, key) => url.searchParams.set(key, value));
      } else {
        url.searchParams.set('view', v || 'ALL');
        url.searchParams.set('tab', t || 'GDA');
        url.searchParams.set('source', s || 'ALL');
        if (items?.length) {
          url.searchParams.set('idents', items.map(item => item.id).join('-'));
        } else {
          url.searchParams.set('idents', 'ALL');
        }
      }
      if (isComplete && search.filters?.length) {
        const columnFilters = filterColumnFilters(search.filters);
        if (columnFilters.length > 0) {
          url.searchParams.set('f', encodeData(columnFilters));
        }
      }
      if (!isComplete && search.searchContext.isEvidence !== undefined) {
        url.searchParams.set('isEvidence', String(search.searchContext.isEvidence));
      }
      url.searchParams.set(isComplete ? 'loadComplete' : 'loadColumns', search.id);
      navigate(url.href.replace(window.location.origin, ''));
      onClose();
      return;
    }

    const targetUrl = search.searchQuery
      ? buildSearchUrl(search)
      : buildSearchUrl({
          ...search,
          filters: (search.filters || []).map((f: Filter) => ({
            ...f,
            isEvidence: search.table === 'Evidence',
          })),
        });
    if (targetUrl) {
      const url = new URL(targetUrl);
      if (search.type === 'complete' && search.searchQuery) {
        url.searchParams.set('loadComplete', search.id);
      }
      navigate(url.href.replace(window.location.origin, ''));
      onClose();
    }
  };

  const shouldShowResetButton = () => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const restrictChemical = isAuthenticated && isAcademic;
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

    const computedDefaultModel = defaultModel(baseDefaultModel, restrictedList);

    const hasCustomVisibility =
      columnVisibilityModel &&
      computedDefaultModel &&
      JSON.stringify(columnVisibilityModel) !== JSON.stringify(computedDefaultModel);

    const defaultColumnOrder = computedDefaultModel
      ? Object.keys(computedDefaultModel).filter(key => computedDefaultModel[key] === true)
      : [];

    const hasCustomOrder =
      columnOrder &&
      columnOrder.length > 0 &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

    return hasCustomVisibility || hasCustomOrder;
  };

  const SIDEBAR_WIDTH_PX = 340;
  const SIDEBAR_WIDTH_PX_MOBILE = '100vw';

  return (
    <aside
      role="complementary"
      aria-label="Table controls"
      className={clsx(
        'fixed right-0 z-40 flex flex-col border-l border-gray-200 bg-white shadow-lg transition-[width] duration-300 ease-out overflow-hidden',
        !isOpen && 'overflow-hidden',
      )}
      style={{
        top: isDesktop ? NAVBAR_HEIGHT_PX : 'auto',
        bottom: !isDesktop ? '0' : 'auto',
        height: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
        width: isOpen ? (isDesktop ? SIDEBAR_WIDTH_PX : SIDEBAR_WIDTH_PX_MOBILE) : 0,
        minWidth: isOpen ? (isDesktop ? SIDEBAR_WIDTH_PX : SIDEBAR_WIDTH_PX_MOBILE) : 0,
        maxWidth: isOpen ? (isDesktop ? SIDEBAR_WIDTH_PX : SIDEBAR_WIDTH_PX_MOBILE) : 0,
      }}
    >
      {/* SidebarHeader */}
      <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50 min-w-0">
        <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0 mb-0">Table controls</h2>
        <button
          ref={closeButtonRef}
          type="button"
          onPointerDownCapture={e => {
            e.preventDefault();
            e.stopPropagation();
            setSidebarOpen(false);
            onClose();
          }}
          className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 flex-shrink-0"
          aria-label="Close sidebar"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable body: fixed width, content never expands sidebar */}
      <div
        className="flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden min-w-0"
        style={{ width: isOpen ? (isDesktop ? SIDEBAR_WIDTH_PX : SIDEBAR_WIDTH_PX_MOBILE) : undefined }}
      >
        {(searchItems.length > 0 || contextSetSearchItems) && (
          <SidebarSection
            title="Search items"
            className="min-h-0 flex-shrink-0"
            defaultOpen={searchItems.length > 0 || !!contextSetSearchItems}
            expandSignal={searchItemsSectionExpandSignal}
          >
            <div className="space-y-2 py-1 min-w-0">
              {contextSetSearchItems && (
                <div className="px-1 min-w-0">
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="Add more: Obesity, LEPR, rs121913279..."
                    value={searchInputValue}
                    onChange={e => setSearchInputValue(e.target.value)}
                    className="w-full min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {searchInputValue.trim().length >= 3 && (
                    <div className="mt-2 relative z-50 min-w-0">
                      <SidebarSearchDropdown
                        value={searchInputValue}
                        setValue={setSearchInputValue}
                        searchItems={pendingItems}
                        setSearchItems={setPendingItems}
                        onDropdownToggle={setSidebarDropdownOpen}
                      />
                    </div>
                  )}
                </div>
              )}
              {searchItems.length > 0 && (
                <>
                  <div className="flex items-center justify-between gap-2 px-3">
                    {showManageGraph && !restrictGraphDisplay && (
                      <SharedButton
                        type="button"
                        variant="secondaryOutline"
                        size="xs"
                        title="Switch view"
                        onClick={() => handleViewChange()}
                      >
                        {selectedView === 'tables' ? (
                          <ChartPieIcon className="w-4 h-4 mr-1" />
                        ) : (
                          <TableCellsIcon className="w-4 h-4 mr-1" />
                        )}
                        View {getViewToggleText(selectedView)}
                      </SharedButton>
                    )}
                    {onClearAllSearchItems && (
                      <SharedButton
                        type="button"
                        variant="secondary"
                        size="xs"
                        onClick={onClearAllSearchItems}
                        title="Clear all and go to home"
                      >
                        <TrashIcon className="w-4 h-4 mr-1" />
                        Clear all
                      </SharedButton>
                    )}
                  </div>
                  <div className="space-y-1 min-w-0">
                    <span
                      onClick={() => setShowCurrentItems(!showCurrentItems)}
                      className="text-xs text-gray-500 ml-3 flex items-center gap-2 cursor-pointer mb-2"
                    >
                      {showCurrentItems ? 'Hide current items' : 'Show current items'}
                      <ChevronDownIcon
                        className="w-4 h-4"
                        style={{ transform: showCurrentItems ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </span>
                    {showCurrentItems &&
                      searchItems.map((item, index) => {
                        const title = getTitle(item);
                        const id = getName(item);
                        const titleStr =
                          typeof title === 'object' && title !== null && 'title' in title
                            ? String((title as { title?: string }).title ?? id)
                            : String(title ?? '');
                        const idStr = String(id ?? '');
                        const showActions =
                          (!isMapping || !!onOpenSearchItemInfo || !!onRemoveSearchItem) &&
                          (typeof title !== 'object' ||
                            (title as { title?: string })?.title !== DATASETS.ALL);
                        return (
                          <div
                            key={idStr || index}
                            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 min-w-0 group text-left"
                          >
                            <span className="flex-1 truncate text-sm font-bold text-blue min-w-0">
                              {[titleStr, idStr].filter(Boolean).join(', ')}
                            </span>
                            {showActions && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                {onOpenSearchItemInfo && (
                                  <button
                                    type="button"
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onOpenSearchItemInfo(item);
                                    }}
                                    title="View details"
                                    data-testid="open-info-button"
                                  >
                                    <GoInfo className="w-5 h-4 stroke-1 text-blue m-1" />
                                  </button>
                                )}
                                {onRemoveSearchItem && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (searchItems.length === 1 && onClearAllSearchItems) {
                                        onClearAllSearchItems();
                                      } else {
                                        onRemoveSearchItem(index);
                                      }
                                    }}
                                    className="p-1 rounded text-blue hover:bg-gray-200 hover:text-red-600"
                                    title="Remove from search"
                                    data-testid="close-info-button"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </SidebarSection>
        )}

        {/* My Score section */}
        {showMyScoreSection && tab && onOpenScoreModal && onApplySavedScore && onClearWeights && (
          <SidebarSection title="My Score" className="min-h-0 flex-shrink-0">
            <div className="space-y-2 py-1 min-w-0">
              <SavedScoresDropdown
                tab={tab}
                onApplyScore={onApplySavedScore}
                onDeleteScore={onDeleteSavedScore ?? (() => {})}
                onConfigureNew={onOpenScoreModal}
                onClearWeights={onClearWeights}
                hasActiveWeights={hasActiveWeights}
                refreshTrigger={refreshScoresDropdown}
                contentOnly
              />
            </div>
          </SidebarSection>
        )}

        <SidebarSection
          title="Sources"
          className="min-h-[140px] max-h-[200px]"
          lockedOverlay={
            !isAuthenticated && view === 'ALL' ? (
              <div className="flex flex-col items-center gap-1.5 p-4 text-center bg-white rounded border">
                <span className="text-xs font-medium text-gray-600">Sign in to customize sources</span>
                <SharedButton size="xs" variant="secondary" onClick={() => navigate('/login')}>
                  Sign in
                </SharedButton>
              </div>
            ) : undefined
          }
        >
          <SidebarSources tab={tab} source={source} sourceWeights={sourceWeights} />
        </SidebarSection>

        {/* Columns section */}
        <SidebarSection
          title="Columns"
          className="min-h-[160px] max-h-[280px]"
          lockedOverlay={
            !isAuthenticated && view === 'ALL' ? (
              <div className="flex flex-col items-center gap-1.5 p-4 text-center bg-white rounded border">
                <span className="text-xs font-medium text-gray-600">Sign in to customize columns</span>
                <SharedButton size="xs" variant="secondary" onClick={() => navigate('/login')}>
                  Sign in
                </SharedButton>
              </div>
            ) : undefined
          }
        >
          {columnPanelToggle && (
            <div className="flex gap-2">
              <div className="flex px-3 py-2 ">
                <SharedButton
                  onClick={() => columnPanelToggle.onChange('summary')}
                  variant={columnPanelToggle.active === 'summary' ? 'secondary' : 'terciary'}
                  size="xxs"
                  weight="md"
                  className="ml-0 mt-0 mr-1"
                >
                  Summary
                </SharedButton>
                <SharedButton
                  onClick={() => columnPanelToggle.onChange('evidence')}
                  variant={columnPanelToggle.active === 'evidence' ? 'secondary' : 'terciary'}
                  size="xxs"
                  weight="md"
                  className="mr-0 ml-1 mt-0"
                >
                  Evidence
                </SharedButton>
              </div>
              {shouldShowResetButton() && (
                <BaseTooltip content="Reset columns to default view" position="bottom">
                  <SharedButton onClick={onResetToDefault} className="mt-2" variant="secondaryText" size="xs">
                    Reset Columns
                  </SharedButton>
                </BaseTooltip>
              )}
            </div>
          )}
          <SidebarColumns
            view={view}
            tab={effectiveSidebarTab}
            source={source}
            isEvidence={isEvidence}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
          />
        </SidebarSection>

        {/* Favorites */}
        <SidebarSection title="Favorites" className="flex-shrink-0" growWithContent>
          <div className="min-w-0 py-1 -mx-1">
            {!isAuthenticated ? (
              <p className="px-4 py-2 text-sm text-gray-500 break-words">
                <a href="/login">Sign in</a> to see your saved searches.
              </p>
            ) : favoritesLoading ? (
              <p className="px-4 py-2 text-sm text-gray-500">Loading…</p>
            ) : favorites.length === 0 ? (
              <p className="px-4 py-2 text-sm text-gray-500 break-words">No saved searches yet.</p>
            ) : (
              <ul className="space-y-0.5 min-w-0">
                {favorites.map(search => {
                  const openLabel =
                    search.type === 'score'
                      ? 'Apply Score'
                      : search.type === 'complete'
                        ? 'Apply All'
                        : search.type === 'columns'
                          ? 'Apply Columns'
                          : 'Open';
                  return (
                    <li key={search.id} className="min-w-0">
                      <button
                        type="button"
                        onClick={() => handleFavoriteClick(search)}
                        className="w-full max-w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors min-w-0 truncate cursor-pointer"
                        title={`${search.name} – ${openLabel}`}
                      >
                        {search.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SidebarSection>

        {/* Saved Columns Section */}
        <SidebarSection title="Saved Columns" className="min-h-0 flex-shrink-0">
          {shouldShowResetButton() && (
            <div className="flex justify-end">
              <BaseTooltip content="Reset columns to default view" position="bottom">
                <SharedButton onClick={onResetToDefault} className="mt-2" variant="secondaryText" size="xs">
                  Reset Columns
                </SharedButton>
              </BaseTooltip>
            </div>
          )}
          {!isAuthenticated ? (
            <p className="px-4 py-2 text-sm text-gray-500 break-words">
              <a href="/login">Sign in</a> to see your saved columns.
            </p>
          ) : !savedColumns.length ? (
            <p className="px-4 py-2 text-sm text-gray-500 break-words">No saved columns yet.</p>
          ) : (
            <div className="space-y-2 py-1 min-w-0">
              <SavedColumnsBar
                view={view}
                tab={tab}
                isEvidence={isEvidence}
                onApplyColumns={columns => onApplySavedColumns?.(columns)}
                onDeleteColumn={onDeleteSavedColumns}
              />
            </div>
          )}
        </SidebarSection>
      </div>
    </aside>
  );
}
